import { Droppable } from '@hello-pangea/dnd';
import { Snowflake } from 'lucide-react';
import KanbanCard from './KanbanCard';
import type { Operacao, Esteira } from '../types';
import { ESTEIRA_DISPLAY, ESTEIRA_COLOR } from '../types';

interface KanbanColumnProps {
  esteira: Esteira;
  operacoes: Operacao[];
  onCardClick: (op: Operacao) => void;
  onEsteiraClick?: (esteira: Esteira) => void;
}

export default function KanbanColumn({ esteira, operacoes, onCardClick, onEsteiraClick }: KanbanColumnProps) {
  const label = ESTEIRA_DISPLAY[esteira];
  const color = ESTEIRA_COLOR[esteira];
  const ativas    = operacoes.filter((op) => op.status !== 'CONGELADA');
  const congeladas = operacoes.filter((op) => op.status === 'CONGELADA');
  // SPEC S1 (E4) - "N com novidade" no cabeçalho quando alguma operação tem evento não visto
  const comNovidade = operacoes.filter((op) => !!op.eventosNaoVistos && op.eventosNaoVistos > 0).length;

  return (
    <div className="flex flex-col bg-[#FFFFFF] rounded-[8px] border border-[#E2E6ED] w-72 shrink-0 shadow-sm h-full overflow-hidden">
      {/* Header neutro - faixa 3px, fundo branco, SEM gradiente */}
      <div
        className={`
          relative flex items-center justify-between px-4 py-3 rounded-t-[8px] bg-white border-b border-[#E2E6ED]
          ${onEsteiraClick ? 'cursor-pointer hover:bg-[#FAFBFC] transition-colors' : ''}
        `}
        onClick={() => onEsteiraClick?.(esteira)}
        title={onEsteiraClick ? `Abrir sub-board: ${label}` : undefined}
      >
        {/* Faixa 3px à esquerda */}
        <div
          className="absolute left-0 top-0 bottom-0 w-[3px] rounded-tl-[8px]"
          style={{ backgroundColor: color }}
        />

        <h2 className="pl-1 font-medium text-[13px] text-[#1A1D23] truncate">{label}</h2>

        <div className="flex items-center gap-1.5 shrink-0">
          {comNovidade > 0 && (
            <span className="text-[10px] font-medium text-blue-700 bg-blue-100 px-1.5 py-[1px] rounded-full whitespace-nowrap">
              {comNovidade} com novidade
            </span>
          )}
          <span className="text-[11px] font-medium text-[#5F6470] bg-[#EAECF2] px-2 py-[2px] rounded-full">
            {operacoes.length}
          </span>
          {onEsteiraClick && (
            <span className="text-[#9AA0AC] text-sm leading-none">›</span>
          )}
        </div>
      </div>

      {/* Cards droppable */}
      <Droppable droppableId={String(esteira)}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`
              flex-1 overflow-y-auto p-3 space-y-2 min-h-[120px] rounded-b-[8px]
              transition-colors duration-150
              ${snapshot.isDraggingOver ? 'bg-[#F7F9FB]' : 'bg-white'}
            `}
          >
            {ativas.map((op, i) => (
              <KanbanCard key={op.id} operacao={op} index={i} onClick={onCardClick} />
            ))}

            {congeladas.length > 0 && (
              <>
                {ativas.length > 0 && (
                  <div className="flex items-center gap-2 my-1">
                    <div className="flex-1 h-px bg-[#EAECF2]" />
                    <span className="inline-flex items-center gap-1 text-[9px] text-[#9AA0AC] uppercase tracking-wide"><Snowflake size={9} /> Pausadas</span>
                    <div className="flex-1 h-px bg-[#EAECF2]" />
                  </div>
                )}
                {congeladas.map((op, i) => (
                  <KanbanCard key={op.id} operacao={op} index={ativas.length + i} onClick={onCardClick} />
                ))}
              </>
            )}

            {operacoes.length === 0 && (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-[11px] text-[#9AA0AC] text-center py-6">
                  Nenhuma operação<br />nesta esteira
                </p>
              </div>
            )}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}
